import { z } from 'zod';

// Netzwerk-Gruppen: mehrere Konten pflegen ein gemeinsames Netzwerk.

export const GroupInvitePolicy = z.enum(['ALL_MEMBERS', 'ADMINS_ONLY']);
export type GroupInvitePolicy = z.infer<typeof GroupInvitePolicy>;

export const GroupRole = z.enum(['OWNER', 'ADMIN', 'MEMBER']);
export type GroupRole = z.infer<typeof GroupRole>;

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v));

export const GroupSummaryDTO = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  invitePolicy: GroupInvitePolicy,
  myRole: GroupRole,
  memberCount: z.number(),
  createdAt: z.string(),
});
export type GroupSummaryDTO = z.infer<typeof GroupSummaryDTO>;

export const GroupMemberDTO = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  photoUrl: z.string().nullable(),
  role: GroupRole,
  joinedAt: z.string(),
  invitedByName: z.string().nullable(),
});
export type GroupMemberDTO = z.infer<typeof GroupMemberDTO>;

export const GroupDetailDTO = GroupSummaryDTO.extend({
  ownerId: z.string(),
  /** Darf der Betrachter Leute einladen (Links/manuell)? */
  canInvite: z.boolean(),
  /** Darf der Betrachter Leute entfernen und Links aller Mitglieder verwalten? */
  canManage: z.boolean(),
  members: z.array(GroupMemberDTO),
});
export type GroupDetailDTO = z.infer<typeof GroupDetailDTO>;

/** Profil eines Gruppenmitglieds, wie die Person es in ihrem eigenen Konto angegeben hat. */
export const GroupMemberProfileDTO = z.object({
  userId: z.string(),
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  company: z.string().nullable(),
  role: z.string().nullable(),
  industry: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  linkedin: z.string().nullable(),
  instagram: z.string().nullable(),
  xing: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  photoUrl: z.string().nullable(),
  pitch: z.string().nullable(),
  offering: z.string().nullable(),
  seeking: z.string().nullable(),
  /** Hat die Person ihr Profil überhaupt schon ausgefüllt? */
  hasProfile: z.boolean(),
  groupRole: GroupRole,
  joinedAt: z.string(),
  /** Ist diese Person bereits im eigenen Netzwerk (per E-Mail erkannt)? Dann die Member-ID. */
  inMyNetworkMemberId: z.string().nullable(),
});
export type GroupMemberProfileDTO = z.infer<typeof GroupMemberProfileDTO>;

export const GroupMemberProfileResponse = z.object({ profile: GroupMemberProfileDTO });
export type GroupMemberProfileResponse = z.infer<typeof GroupMemberProfileResponse>;

export const GroupInviteDTO = z.object({
  id: z.string(),
  token: z.string(),
  label: z.string().nullable(),
  email: z.string().nullable(),
  maxUses: z.number().nullable(),
  uses: z.number(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  createdByName: z.string(),
  createdAt: z.string(),
  /** Noch einlösbar (nicht widerrufen, nicht abgelaufen, nicht aufgebraucht). */
  active: z.boolean(),
});
export type GroupInviteDTO = z.infer<typeof GroupInviteDTO>;

// --- Eingaben ---

export const GroupCreateInput = z.object({
  name: z.string().trim().min(1, 'Bitte gib der Gruppe einen Namen.').max(120),
  description: optText(1000),
  invitePolicy: GroupInvitePolicy.default('ADMINS_ONLY'),
});
export type GroupCreateInput = z.input<typeof GroupCreateInput>;

export const GroupUpdateInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: optText(1000),
  invitePolicy: GroupInvitePolicy.optional(),
});
export type GroupUpdateInput = z.input<typeof GroupUpdateInput>;

export const GroupMemberRoleInput = z.object({
  role: z.enum(['ADMIN', 'MEMBER']),
});
export type GroupMemberRoleInput = z.infer<typeof GroupMemberRoleInput>;

export const GroupInviteCreateInput = z.object({
  label: optText(120),
  /** null/leer = dauerhaft (unbegrenzt viele Nutzungen). */
  maxUses: z.number().int().min(1).max(10_000).nullable().optional(),
  /** Gültig für x Tage; null/leer = ohne Ablauf. */
  expiresInDays: z.number().int().min(1).max(365).nullable().optional(),
});
export type GroupInviteCreateInput = z.input<typeof GroupInviteCreateInput>;

/** Manuell einladen: per E-Mail. Mit Konto → direkt Mitglied, sonst persönliche Einladungsmail. */
export const GroupManualInviteInput = z.object({
  email: z.string().trim().toLowerCase().email('Bitte eine gültige E-Mail-Adresse angeben.').max(200),
});
export type GroupManualInviteInput = z.input<typeof GroupManualInviteInput>;

export const GroupManualInviteResponse = z.object({
  /** added = hatte schon ein Konto und ist jetzt Mitglied; invited = Einladungsmail verschickt. */
  result: z.enum(['added', 'invited', 'already_member']),
  invite: GroupInviteDTO.nullable(),
});
export type GroupManualInviteResponse = z.infer<typeof GroupManualInviteResponse>;

// --- Antworten ---

export const GroupListResponse = z.object({ groups: z.array(GroupSummaryDTO) });
export type GroupListResponse = z.infer<typeof GroupListResponse>;

export const GroupDetailResponse = z.object({ group: GroupDetailDTO });
export type GroupDetailResponse = z.infer<typeof GroupDetailResponse>;

export const GroupInviteListResponse = z.object({ invites: z.array(GroupInviteDTO) });
export type GroupInviteListResponse = z.infer<typeof GroupInviteListResponse>;

export const GroupInviteResponse = z.object({ invite: GroupInviteDTO });
export type GroupInviteResponse = z.infer<typeof GroupInviteResponse>;

/** Öffentliche Vorschau eines Einladungslinks (/g/<token>). */
export const GroupInvitePreviewResponse = z.object({
  group: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    memberCount: z.number(),
  }),
  invitedBy: z.string(),
  /** valid | revoked | expired | used_up */
  status: z.enum(['valid', 'revoked', 'expired', 'used_up']),
  /** Nur bei angemeldetem Betrachter: ist er schon drin? */
  alreadyMember: z.boolean(),
  loggedIn: z.boolean(),
});
export type GroupInvitePreviewResponse = z.infer<typeof GroupInvitePreviewResponse>;

export const GroupInviteAcceptResponse = z.object({
  groupId: z.string(),
  alreadyMember: z.boolean(),
});
export type GroupInviteAcceptResponse = z.infer<typeof GroupInviteAcceptResponse>;
